/**
 * MSAL Context Provider
 * 
 * Provides MSAL instance and authentication state to the app
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AccountInfo } from '@azure/msal-browser';
import { authService } from '@/services/authService';

interface MsalContextType {
  isInitialized: boolean;
  isAuthenticated: boolean;
  account: AccountInfo | null;
  accessToken: string | null;
  isLoading: boolean;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
}

const MsalContext = createContext<MsalContextType | undefined>(undefined);

interface MsalProviderProps {
  children: ReactNode;
}

export const MsalProvider: React.FC<MsalProviderProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Initialize MSAL and auth service
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('='.repeat(80));
      console.log('[MsalProvider] 🚀 Starting auth service initialization...');
      
      try {
        setIsLoading(true);
        setError(null);

        console.log('[MsalProvider] Calling authService.initialize()...');
        await authService.initialize();
        console.log('[MsalProvider] ✅ authService initialized successfully');
        setIsInitialized(true);

        // Check if user is already authenticated
        console.log('[MsalProvider] Checking for existing account...');
        const currentAccount = authService.getAccount();
        console.log('[MsalProvider] Account found:', !!currentAccount);
        
        if (currentAccount) {
          console.log('[MsalProvider] Account details:', {
            username: currentAccount.username,
            name: currentAccount.name,
            homeAccountId: currentAccount.homeAccountId
          });
          setAccount(currentAccount);
          setIsAuthenticated(true);
          console.log('[MsalProvider] ✅ User already authenticated:', currentAccount.username);

          // Try to acquire token
          console.log('[MsalProvider] Attempting to acquire token on init...');
          const token = await authService.acquireToken();
          if (token) {
            setAccessToken(token);
            console.log('[MsalProvider] ✅ Token acquired on init, length:', token.length);
          } else {
            console.log('[MsalProvider] ⚠️ Failed to acquire token on init');
          }
        } else {
          console.log('[MsalProvider] ⚠️ No authenticated user found');
        }
      } catch (err) {
        console.error('[MsalProvider] ❌ Initialization error:', err);
        console.error('[MsalProvider] Error details:', JSON.stringify(err, null, 2));
        setError(err instanceof Error ? err.message : 'Failed to initialize authentication');
      } finally {
        console.log('[MsalProvider] Setting isLoading to false');
        setIsLoading(false);
        console.log('='.repeat(80));
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async () => {
    console.log('='.repeat(80));
    console.log('[MsalProvider] 🔐 Login function called');
    
    try {
      setIsLoading(true);
      setError(null);

      const isTeamsContext = authService.isTeams();
      console.log('[MsalProvider] Context type:', isTeamsContext ? '📱 Teams' : '🌐 Browser');

      if (isTeamsContext) {
        // Teams SSO - acquire token directly
        console.log('[MsalProvider] Teams context detected, acquiring SSO token...');
        const token = await authService.acquireToken();
        
        if (token) {
          console.log('[MsalProvider] ✅ Token acquired, length:', token.length);
          setAccessToken(token);
          
          const currentAccount = authService.getAccount();
          console.log('[MsalProvider] Account lookup:', currentAccount ? '✓ Found' : '✗ Not found');
          
          setAccount(currentAccount);
          setIsAuthenticated(true);
          setIsLoading(false); // FIX: Reset loading state on Teams SSO success
          console.log('[MsalProvider] ✅ Teams SSO login successful');
        } else {
          console.error('[MsalProvider] ❌ Failed to acquire Teams SSO token');
          setIsLoading(false); // Also reset on failure
          throw new Error('Failed to acquire Teams SSO token');
        }
      } else {
        // Browser - use redirect flow
        console.log('[MsalProvider] Browser context, redirecting to Azure AD...');
        await authService.loginWithRedirect();
        console.log('[MsalProvider] Redirect initiated (page will reload)');
        // Note: After redirect, page will reload and useEffect will handle the rest
      }
    } catch (err) {
      console.error('[MsalProvider] ❌ Login error:', err);
      console.error('[MsalProvider] Error details:', JSON.stringify(err, null, 2));
      setError(err instanceof Error ? err.message : 'Login failed');
      setIsLoading(false);
    } finally {
      console.log('='.repeat(80));
    }
  };

  // Logout function
  const logout = async () => {
    console.log('='.repeat(80));
    console.log('[MsalProvider] 🚪 Logout function called');
    
    try {
      setIsLoading(true);
      console.log('[MsalProvider] Calling authService.logout()...');
      await authService.logout();
      console.log('[MsalProvider] ✅ authService.logout() completed');
      
      console.log('[MsalProvider] Clearing state...');
      setAccount(null);
      setAccessToken(null);
      setIsAuthenticated(false);
      console.log('[MsalProvider] ✅ Logout successful, state cleared');
    } catch (err) {
      console.error('[MsalProvider] ❌ Logout error:', err);
      console.error('[MsalProvider] Error details:', JSON.stringify(err, null, 2));
      setError(err instanceof Error ? err.message : 'Logout failed');
    } finally {
      setIsLoading(false);
      console.log('='.repeat(80));
    }
  };

  // Get or refresh token
  const getToken = async (): Promise<string | null> => {
    console.log('[MsalProvider] 🔑 getToken function called');
    
    try {
      console.log('[MsalProvider] Calling authService.acquireToken()...');
      const token = await authService.acquireToken();
      
      if (token) {
        console.log('[MsalProvider] ✅ Token acquired/refreshed, length:', token.length);
        setAccessToken(token);
      } else {
        console.log('[MsalProvider] ⚠️ No token acquired');
      }
      
      return token;
    } catch (err) {
      console.error('[MsalProvider] ❌ Token acquisition error:', err);
      console.error('[MsalProvider] Error details:', JSON.stringify(err, null, 2));
      setError(err instanceof Error ? err.message : 'Failed to get token');
      return null;
    }
  };

  const contextValue: MsalContextType = {
    isInitialized,
    isAuthenticated,
    account,
    accessToken,
    isLoading,
    error,
    login,
    logout,
    getToken,
  };

  return (
    <MsalContext.Provider value={contextValue}>
      {children}
    </MsalContext.Provider>
  );
};

/**
 * Hook to use MSAL context
 */
export const useMsal = (): MsalContextType => {
  const context = useContext(MsalContext);
  if (context === undefined) {
    throw new Error('useMsal must be used within a MsalProvider');
  }
  return context;
};
